import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, OutlineButton } from "components/button";

interface ScheduleModalProps {
  isOpen: boolean;
  campaignName: string;
  recipientCount: number | null;
  isScheduling: boolean;
  onConfirm: (scheduledAt: string, timezone: string) => void;
  onCancel: () => void;
}

const TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "Eastern (ET)" },
  { value: "America/Chicago", label: "Central (CT)" },
  { value: "America/Denver", label: "Mountain (MT)" },
  { value: "America/Los_Angeles", label: "Pacific (PT)" },
  { value: "America/Anchorage", label: "Alaska (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii (HT)" },
  { value: "UTC", label: "UTC" },
];

function getLocalTimezone(): string {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return TIMEZONE_OPTIONS.find((o) => o.value === tz)?.value || "America/New_York";
}

function getMinDateTime(): string {
  const now = new Date();
  now.setMinutes(now.getMinutes() + 5);
  return now.toISOString().slice(0, 16);
}

const ScheduleModal = ({
  isOpen,
  campaignName,
  recipientCount,
  isScheduling,
  onConfirm,
  onCancel,
}: ScheduleModalProps) => {
  const { t } = useTranslation();
  const tBase = "views.outreach.campaigns.schedule";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [timezone, setTimezone] = useState(getLocalTimezone);

  if (!isOpen) {
    return null;
  }

  const handleConfirm = () => {
    if (!date || !time) {
      return;
    }
    const localDateTime = `${date}T${time}:00`;
    const scheduledAt = new Date(
      new Date(localDateTime).toLocaleString("en-US", { timeZone: timezone })
    ).toISOString();

    // Build a proper ISO string in the chosen timezone
    // The backend accepts an ISO datetime string + timezone name
    const isoString = new Date(`${date}T${time}:00`).toISOString();
    onConfirm(isoString, timezone);
  };

  const isValid = date && time;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-xl font-bold mb-4">{tr("title")}</h2>

        <div className="space-y-4 mb-6">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">{tr("campaignName")}:</span>
            <span className="font-medium">{campaignName}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">{tr("recipients")}:</span>
            <span className="font-bold text-[#ff4f00]">
              {recipientCount ?? "--"}
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {tr("date")}
            </label>
            <input
              type="date"
              className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4f00]"
              value={date}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {tr("time")}
            </label>
            <input
              type="time"
              className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4f00]"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {tr("timezone")}
            </label>
            <select
              className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#ff4f00]"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            >
              {TIMEZONE_OPTIONS.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <OutlineButton
            size="small"
            onClick={onCancel}
            disabled={isScheduling}
          >
            {tr("cancel")}
          </OutlineButton>
          <Button
            primary
            size="small"
            onClick={handleConfirm}
            disabled={!isValid || isScheduling}
            isLoading={isScheduling}
          >
            {isScheduling ? tr("scheduling") : tr("confirmSchedule")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleModal;
